import { readLimitedBytes, runWithDeadline } from './asyncRequest';
import { parseKmlInWorker } from './kmlWorkerClient';
import { attachKmlMapLayer } from './kmlMapLayer';

export function resolveKmlUrl(value, base = process.env.REACT_APP_WEBAPI_HOST + '/') {
    const url = new URL(value, base), api = new URL(process.env.REACT_APP_WEBAPI_HOST || window.location.origin);
    // Historical records use HTTP for this server; its HTTPS endpoint is available.
    if (url.protocol === 'http:' && url.host === api.host && api.protocol === 'https:') url.protocol = 'https:';
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) throw new Error('KML 網址必須使用 HTTP 或 HTTPS，且不可包含帳密。');
    if (window.location.protocol === 'https:' && url.protocol !== 'https:') throw new Error('KML 網址須改用 HTTPS，避免瀏覽器封鎖混合內容。');
    return url.href;
}

export function projectKmlUrl(layer, projectId) {
    if (typeof layer?.filename !== 'string' || !layer.filename.trim()) throw new Error('KML 缺少檔名或網址。');
    if (layer.isFilenameUrl || /^https?:\/\//i.test(layer.filename)) return resolveKmlUrl(layer.filename);
    const id = encodeURIComponent(String(layer.projectId || projectId).toUpperCase());
    return resolveKmlUrl(`/projectKmlFiles/${id}/${encodeURIComponent(layer.filename)}`);
}

export async function loadKmlLayer(map, url, { signal, fitBounds = false, budget = { features: 0 }, onBounds = () => {}, onWarning = () => {} } = {}) {
    const disposers = [], objectUrls = [], seen = new Set(), queue = [url];
    let totalFeatures = 0, disposed = false;
    const dispose = () => {
        if (disposed) return;
        disposed = true;
        signal?.removeEventListener('abort', dispose);
        disposers.forEach(cleanup => { try { cleanup(); } catch { /* Preserve project navigation. */ } });
        objectUrls.forEach(objectUrl => URL.revokeObjectURL(objectUrl));
    };
    signal?.addEventListener('abort', dispose, { once: true });
    try {
        while (queue.length) {
            if (signal?.aborted) throw Object.assign(new Error('已取消載入。'), { name: 'AbortError' });
            const sourceUrl = queue.shift();
            if (seen.has(sourceUrl)) continue;
            if (seen.size >= 12) { onWarning('KML 連結超過 12 份上限，已停止延伸載入。'); break; }
            seen.add(sourceUrl);
            try {
                const buffer = await runWithDeadline(async requestSignal => {
                    // Never forward the GeoNow JWT to an external KML/asset host.
                    let response;
                    try { response = await fetch(sourceUrl, { signal: requestSignal, credentials: 'omit' }); }
                    catch (error) {
                        if (error.name === 'AbortError') throw error;
                        throw new Error('無法讀取 KML，請確認網址、網路與伺服器的跨網域設定（CORS）。');
                    }
                    if (!response.ok) throw new Error(`KML 下載失敗（HTTP ${response.status}）。`);
                    return readLimitedBytes(response, 8 * 1024 * 1024);
                }, { signal, timeoutMs: 15000 });
                const parsed = await parseKmlInWorker(buffer, { signal });
                if (signal?.aborted) throw Object.assign(new Error('已取消載入。'), { name: 'AbortError' });
                const assets = new Map(parsed.assets.map(({ path, data }) => {
                    const extension = path.split('.').pop().toLowerCase();
                    const types = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp' };
                    const assetUrl = URL.createObjectURL(new Blob([data], { type: types[extension] }));
                    objectUrls.push(assetUrl); return [path, assetUrl];
                }));
                parsed.geojson.features.forEach(feature => {
                    const properties = feature.properties || {};
                    if (properties.icon) {
                        try { properties.icon = properties.icon.startsWith('kmz:') ? assets.get(properties.icon.slice(4)) : resolveKmlUrl(properties.icon, sourceUrl); }
                        catch { delete properties.icon; onWarning('部分 KML 影像網址無效，已略過該影像。'); }
                    }
                });
                totalFeatures += parsed.geojson.features.length;
                budget.features += parsed.geojson.features.length;
                if (totalFeatures > 20000) throw new Error('KML 圖徵總數超過上限，請先簡化圖層。');
                if (budget.features > 40000) throw new Error('專案 KML 圖徵總數超過 40000 上限，請拆分或簡化圖層。');
                disposers.push(await attachKmlMapLayer(map, parsed.geojson, { signal, fitBounds, onWarning }));
                if (parsed.bounds && !signal?.aborted) onBounds(parsed.bounds);
                parsed.warnings.forEach(onWarning);
                for (const link of parsed.links) {
                    try { const resolved = resolveKmlUrl(link, sourceUrl); if (!seen.has(resolved)) queue.push(resolved); }
                    catch { onWarning('部分 KML 連結網址無效，已略過該連結。'); }
                }
            } catch (error) {
                if (signal?.aborted || seen.size === 1) throw error;
                onWarning('部分 KML 連結載入失敗，已保留其他成功載入的圖層。');
            }
        }
        return dispose;
    } catch (error) { dispose(); throw error; }
}
